/**
 * Agent hand-offs wire contract (spec #46 §2 + #44's accepted delta 4:
 * `GET /api/agents/handoffs` list and `POST /api/agents/handoffs/{id}/resolve`).
 *
 * The workspace reads the two HUMAN types only — `tool_request` and
 * `review_request`, the two #46 itself marks "consumed by: human". The
 * three cycle-consumed types are never rendered as questions: they are the
 * engine's own input, and showing them would ask a person to answer
 * something no one is waiting on.
 */

/** The closed five-type vocabulary of #46 §2. */
export type HandoffType =
  | 'finding_for_owner'
  | 'belief_challenge'
  | 'prerequisite_output'
  | 'tool_request'
  | 'review_request';

/** The two types a human answers — the only ones the questions lane shows. */
export const HUMAN_HANDOFF_TYPES: HandoffType[] = [
  'tool_request',
  'review_request',
];

export type HandoffStatus =
  | 'open'
  | 'checked_out'
  | 'done'
  | 'declined'
  | 'expired';

export type HandoffFromKind = 'agent' | 'specialist' | 'human';

export interface HandoffItem {
  id: string;
  from_kind: HandoffFromKind;
  from_key: string;
  to_domain: string;
  type: HandoffType;
  /** Typed refs + a bounded note; never free reasoner text (#46 §2). */
  payload: Record<string, unknown>;
  status: HandoffStatus;
  created_at: string;
  expires_at: string | null;
  /** A DERIVED view flag, not a persisted state: the recipient is not
   * runnable, or a connector the payload names failed the freshness gate.
   * A blocked item stays `open` and is never checked out. */
  blocked: boolean;
  blocked_reason: string | null;
}

export interface HandoffListResponse {
  items: HandoffItem[];
  total: number;
  has_more: boolean;
}

export interface HandoffListQuery {
  to_domain?: string;
  status?: HandoffStatus;
  types?: HandoffType[];
  limit?: number;
  offset?: number;
}

export type HandoffOutcome = 'done' | 'declined';

export interface HandoffResolveRequest {
  outcome: HandoffOutcome;
  reason?: string;
}

export interface HandoffResolveResponse {
  id: string;
  status: HandoffStatus;
}
